import React, { useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../components/ui/sheet';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Minus, Plus, X, ShoppingBag, Tag, Check } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { Link } from 'react-router-dom';

export default function CartDrawer() {
  const {
    cart, isOpen, setIsOpen, cartSubtotal, cartTotal, discount,
    updateQuantity, removeItem, checkout, loading,
    promoCode, setPromoCode, promoApplied, promoError, applyPromo, removePromo,
  } = useCart();
  const items = cart.items || [];
  const [promoInput, setPromoInput] = useState('');
  const [applying, setApplying] = useState(false);

  const handleApplyPromo = async () => {
    if (!promoInput.trim()) return;
    setApplying(true);
    await applyPromo(promoInput.trim());
    setApplying(false);
  };

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetContent className="bg-[#0A0A0A] border-l border-[#C4A35A]/15 w-full sm:max-w-md flex flex-col" data-testid="cart-drawer">
        <SheetHeader className="pb-4 border-b border-[#C4A35A]/10">
          <SheetTitle className="font-['Cormorant_Garamond'] text-2xl text-[#E8D5A3]">Your Bag</SheetTitle>
          <SheetDescription className="sr-only">Shopping cart contents</SheetDescription>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-4 text-center">
            <ShoppingBag size={48} className="text-[#2A2520]" />
            <p className="text-[#9A8E7A] text-sm">Your bag is empty</p>
            <Button
              onClick={() => setIsOpen(false)}
              asChild
              className="bg-[#C4A35A] hover:bg-[#D4B86A] text-[#0A0A0A] rounded-full px-8 font-medium"
              data-testid="continue-shopping-btn"
            >
              <Link to="/shop">Continue Shopping</Link>
            </Button>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto py-4 space-y-4" data-testid="cart-items-list">
              {items.map((item) => (
                <div key={item.product_id} className="flex gap-3 p-3 bg-[#141414] rounded-lg border border-[#2A2520]" data-testid={`cart-item-${item.product_id}`}>
                  {item.image && (
                    <img src={item.image} alt={item.name} className="w-20 h-20 object-cover rounded-md" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#F0E6D0] truncate">{item.name}</p>
                    <p className="text-sm text-[#C4A35A] font-medium mt-0.5">${item.price.toFixed(2)}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <button
                        onClick={() => updateQuantity(item.product_id, item.quantity - 1)}
                        className="w-7 h-7 rounded-full border border-[#2A2520] flex items-center justify-center hover:bg-[#1E1E1E] hover:border-[#C4A35A]/30 transition-colors text-[#9A8E7A]"
                        data-testid={`decrease-qty-${item.product_id}`}
                      >
                        <Minus size={12} />
                      </button>
                      <span className="text-sm font-medium w-6 text-center text-[#F0E6D0]" data-testid={`item-qty-${item.product_id}`}>{item.quantity}</span>
                      <button
                        onClick={() => updateQuantity(item.product_id, item.quantity + 1)}
                        className="w-7 h-7 rounded-full border border-[#2A2520] flex items-center justify-center hover:bg-[#1E1E1E] hover:border-[#C4A35A]/30 transition-colors text-[#9A8E7A]"
                        data-testid={`increase-qty-${item.product_id}`}
                      >
                        <Plus size={12} />
                      </button>
                    </div>
                  </div>
                  <button
                    onClick={() => removeItem(item.product_id)}
                    className="text-[#9A8E7A] hover:text-[#C4A35A] transition-colors self-start"
                    data-testid={`remove-item-${item.product_id}`}
                  >
                    <X size={16} />
                  </button>
                </div>
              ))}
            </div>

            <div className="border-t border-[#C4A35A]/10 pt-4 space-y-3">
              {/* Promo Code Section */}
              {promoApplied ? (
                <div className="flex items-center justify-between bg-[#C4A35A]/10 border border-[#C4A35A]/20 rounded-lg px-3 py-2" data-testid="promo-applied">
                  <div className="flex items-center gap-2">
                    <Check size={14} className="text-[#C4A35A]" />
                    <span className="text-xs font-medium text-[#C4A35A]">{promoApplied.code}</span>
                    <span className="text-xs text-[#9A8E7A]">({promoApplied.discount_percent}% off)</span>
                  </div>
                  <button
                    onClick={removePromo}
                    className="text-[#9A8E7A] hover:text-[#C4A35A] transition-colors"
                    data-testid="remove-promo-btn"
                  >
                    <X size={14} />
                  </button>
                </div>
              ) : (
                <div data-testid="promo-code-section">
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Tag size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9A8E7A]/50" />
                      <Input
                        type="text"
                        placeholder="Promo code"
                        value={promoInput}
                        onChange={(e) => setPromoInput(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleApplyPromo()}
                        className="pl-8 h-9 rounded-lg border-[#2A2520] bg-[#141414] text-[#F0E6D0] placeholder:text-[#9A8E7A]/40 focus:border-[#C4A35A] text-xs"
                        data-testid="promo-code-input"
                      />
                    </div>
                    <Button
                      onClick={handleApplyPromo}
                      disabled={applying || !promoInput.trim()}
                      className="h-9 px-4 rounded-lg bg-[#1E1E1E] hover:bg-[#2A2520] text-[#C4A35A] border border-[#2A2520] text-xs font-medium"
                      data-testid="apply-promo-btn"
                    >
                      {applying ? '...' : 'Apply'}
                    </Button>
                  </div>
                  {promoError && <p className="text-xs text-red-400 mt-1" data-testid="promo-error">{promoError}</p>}
                </div>
              )}

              {/* Totals */}
              <div className="space-y-1.5">
                <div className="flex justify-between items-center">
                  <span className="text-xs text-[#9A8E7A]">Subtotal</span>
                  <span className="text-sm text-[#F0E6D0]">${cartSubtotal.toFixed(2)}</span>
                </div>
                {discount > 0 && (
                  <div className="flex justify-between items-center" data-testid="discount-row">
                    <span className="text-xs text-[#C4A35A]">Discount ({promoApplied.discount_percent}%)</span>
                    <span className="text-sm text-[#C4A35A]">-${discount.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between items-center pt-1.5 border-t border-[#2A2520]">
                  <span className="text-sm text-[#9A8E7A]">Total</span>
                  <span className="text-lg font-['Cormorant_Garamond'] font-semibold text-[#E8D5A3]" data-testid="cart-total">
                    ${cartTotal.toFixed(2)}
                  </span>
                </div>
              </div>

              <Button
                onClick={checkout}
                disabled={loading}
                className="w-full bg-[#C4A35A] hover:bg-[#D4B86A] text-[#0A0A0A] rounded-full h-12 text-sm font-bold"
                data-testid="checkout-button"
              >
                {loading ? 'Processing...' : 'Checkout'}
              </Button>
              <button
                onClick={() => setIsOpen(false)}
                className="w-full text-center text-sm text-[#9A8E7A] hover:text-[#C4A35A] transition-colors"
                data-testid="keep-shopping-btn"
              >
                Keep Shopping
              </button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
